// FE#216 — the "refresh contacts from WhatsApp" re-sync behavior, extracted from
// UnifiedContactSelector.handleRefresh so it is unit-testable without a render
// harness. Deps (dispatch + the two thunk action-creators) are INJECTED so this
// module pulls in no redux/thunk/@whatsapp-web weight — the component wires the
// real thunks, the test wires mocks, and both exercise THIS code.

export interface ContactResyncOptions {
  searchValue: string;
  typeFilter: string;
  isExtension: boolean;
}

export interface Unwrappable {
  unwrap: () => Promise<unknown>;
}

export interface ContactResyncDeps {
  /** redux dispatch — returns a thunk promise exposing `.unwrap()`. */
  dispatch: (action: unknown) => Unwrappable;
  /** DBWaContactsCacheThunks.find — the web forced-resync action creator. */
  webFind: (args: unknown) => unknown;
  /** loadAllContactsAndChatsInExtensionThunk — the extension direct-pull creator. */
  extensionResync: () => unknown;
}

/**
 * The web forced-resync `find` args. `$forceContactsUpdate: true` is the engine
 * command that makes the backend do a live WhatsApp pull and repopulate the
 * cache doc; `$skip:0/$limit:20` re-reads page 1 for the current query so a
 * just-added contact surfaces. `typeFilter` mirrors the search-driven fetch.
 */
export function buildWebResyncArgs(searchValue: string, typeFilter: string) {
  return {
    $skip: 0,
    $limit: 20,
    search: searchValue || undefined,
    $forceContactsUpdate: true as const,
    ...(typeFilter !== 'all' ? { typeFilter } : {}),
  };
}

/**
 * AWAIT the real re-sync. Extension context → direct WA pull; web → the forced
 * cache find. Throws on failure so the caller can skip the last-synced stamp.
 */
export async function performContactResync(
  deps: ContactResyncDeps,
  opts: ContactResyncOptions,
): Promise<{ refreshFailed: boolean }> {
  if (opts.isExtension) {
    await deps.dispatch(deps.extensionResync()).unwrap();
    return { refreshFailed: false };
  }
  const result = await deps.dispatch(deps.webFind(buildWebResyncArgs(opts.searchValue, opts.typeFilter))).unwrap();
  // #297 — the backend flags a failed/partial/empty pod pull with `refreshFailed` (it still serves
  // the stale cache). DBServiceReducers' find thunk copies the flag onto the list it resolves with.
  // The find thunk swallows a thrown find and resolves undefined, so a non-array result is a failure too.
  if (!Array.isArray(result)) return { refreshFailed: true };
  return { refreshFailed: (result as { refreshFailed?: boolean }).refreshFailed === true };
}

/**
 * Run the re-sync and resolve the timestamp to stamp as `lastSyncedAt` — the
 * given `nowMs` on success, or `null` on failure (so a failed/aborted sync
 * leaves the previous timestamp intact). This is exactly the success-only
 * stamping rule the component applies.
 */
export async function resyncContacts(
  deps: ContactResyncDeps,
  opts: ContactResyncOptions,
  nowMs: number,
): Promise<number | null> {
  try {
    const { refreshFailed } = await performContactResync(deps, opts);
    // A refresh the backend flagged as failed/partial must not read as "synced just now".
    return refreshFailed ? null : nowMs;
  } catch {
    return null;
  }
}

/**
 * #297 — which status line the refresh button shows: in flight wins, then a failed last attempt
 * (a visible retry hint, since a failed/partial refresh keeps the old "last synced" stamp), then
 * the last-synced label. The failed state clears when the next attempt starts.
 */
export type SyncHint = 'syncing' | 'failed' | 'synced' | null;
export function syncHintFor(a: { isRefreshing: boolean; lastAttemptFailed: boolean; lastSyncedLabel: string | null | undefined }): SyncHint {
  if (a.isRefreshing) return 'syncing';
  if (a.lastAttemptFailed) return 'failed';
  return a.lastSyncedLabel ? 'synced' : null;
}
